import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams, Outlet } from 'react-router-dom';
import { fetchVacancies, createVacancy, updateVacancy, deleteVacancy, updateVacancyStatus, updateVacancyStatusOptimistic, revertVacancyStatus, fetchVacanciesFunnelStatuses, setShowAllVacancies, fetchVacancyFunnelStatusesById, fetchVacancyHistory } from '../../redux/vacanciesSlice';
import { fetchCandidates } from '../../redux/candidatesSlice';
import { fetchUsers } from '../../redux/userOrganisationsSlice';
import { fetchCandidatesByVacancy } from '../../redux/vacancyCandidatesSlice';
import ListView from '../../components/Vacancies/ListView';
import KanbanView from '../../components/Vacancies/KanbanView';
import CreateModal from '../../components/Vacancies/CreateModal';
import EditModal from '../../components/Vacancies/EditModal';
import ViewModal from '../../components/Vacancies/ViewModal';
import styles from '../../components/Vacancies/Vacancies.module.css';
import { Button, Radio, message, Space, Dropdown, Menu, Checkbox, Popover, Tooltip } from 'antd';
import { DownOutlined, SettingOutlined } from '@ant-design/icons';
import moment from "moment";
import { columnLabels, STATUSES } from '../../constants/vacancyColumnLabels'
import CreateFromTemplateModal from '../../components/Vacancies/CreateFromTemplateModal';
import { canCreateVacancyFromRequest, canCreateVacancyWithoutRequest, canEditVacancy, canChangeVacancyStatus } from '../../utils/vacancyPermissions';

const allColumnKeys = Object.keys(columnLabels);

const defaultColumns = [
  'id',
  'job_title',
  'positions_count',
  'organisation_id',
  'department_id',
  'assignee_id',
  'planned_close_date',
  'status',
  'actions'
];

const VacanciesPage = () => {
  const { orgId, role } = useSelector((state) => state.auth);
  console.log('role', role);
  const dispatch = useDispatch();
  const { list: vacancies, loading: vacanciesLoading, showAllVacancies, vacancyFunnelStatuses, isLoading: vacanciesIsLoading, error: vacanciesError, totalPages, totalItems } = useSelector(state => state.vacancies);
  const { organisations, loading: orgsLoading } = useSelector(state => state.organisationDepartment);
  const { list: users, loading: usersLoading } = useSelector(state => state.userOrganisations);
  const { funnelStatuses, history } = useSelector(state => state.vacancies);
  const { list: allCandidates, isLoading: candidatesLoading } = useSelector(state => state.candidates);
  const { candidatesByVacancy } = useSelector(state => state.vacancyCandidates);
  const [searchParams, setSearchParams] = useSearchParams();

  const [viewMode, setViewMode] = useState('list');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedVacancy, setSelectedVacancy] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState(defaultColumns);
  const [selectedStatuses, setSelectedStatuses] = useState(Object.keys(STATUSES));
  const [isCreateFromTemplateModalOpen, setIsCreateFromTemplateModalOpen] = useState(false);
  const [columnsDropdownOpen, setColumnsDropdownOpen] = useState(false);
  const [statusesDropdownOpen, setStatusesDropdownOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({});

  useEffect(() => {
    const orgIdToFetch = showAllVacancies ? "all" : orgId;
    const skip = (currentPage - 1) * pageSize;

    const backendFilters = {};
    const keyMapping = {
      'organisation_id': 'organisation',
      'department_id': 'department'
    };

    Object.keys(filters).forEach(key => {
      const value = filters[key];

      if (value === null || value === undefined || value === '') {
        return;
      }

      // const backendKey = keyMapping[key] || key;
      const backendKey = key;

      if (Array.isArray(value)) {
        const filteredArray = value.filter(v => v !== null && v !== undefined && v !== '');
        if (filteredArray.length > 0) {
          backendFilters[backendKey] = filteredArray.join(',');
        }
      } else if (typeof value === 'string' || typeof value === 'number') {
        backendFilters[backendKey] = value;
      } else if (typeof value === 'boolean') {
        backendFilters[backendKey] = value;
      }
    });

    dispatch(fetchVacancies({ skip, limit: pageSize, org_id: orgIdToFetch, filters: backendFilters }));
  }, [dispatch, orgId, showAllVacancies, currentPage, pageSize, filters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [showAllVacancies, filters]);

  useEffect(() => {
    dispatch(fetchUsers());
    dispatch(fetchVacanciesFunnelStatuses());
    dispatch(fetchCandidates({ skip: 0, limit: 1000, org_id: orgId }));
  }, [dispatch, orgId]);

  useEffect(() => {
    if (isViewModalOpen && selectedVacancy?.id) {
      const vacancyId = selectedVacancy.id;
      dispatch(fetchVacancyFunnelStatusesById(vacancyId));
      dispatch(fetchVacancyHistory({ id: vacancyId, organisationId: orgId }));
      dispatch(fetchCandidatesByVacancy(vacancyId));
    }
  }, [dispatch, isViewModalOpen, selectedVacancy, orgId]);

  useEffect(() => {
    const openVacancyId = searchParams.get('openVacancy');
    if (openVacancyId && vacancies.length > 0) {
      const vacancyToOpen = vacancies.find(v => v.id === parseInt(openVacancyId));
      if (vacancyToOpen) {
        handleView(vacancyToOpen);
        setSearchParams({});
      }
    }
  }, [searchParams, vacancies, setSearchParams]);

  const handleCreate = async (formData) => {
    try {
      const formattedData = {
        ...formData,
        opening_date: formData.opening_date ? moment(formData.opening_date).format('YYYY-MM-DD') : null,
        closing_date: formData.closing_date ? moment(formData.closing_date).format('YYYY-MM-DD') : null,
        exit_date: formData.exit_date ? moment(formData.exit_date).format('YYYY-MM-DD') : null,
        scheduled_start_date: formData.scheduled_start_date ? moment(formData.scheduled_start_date).format('YYYY-MM-DD') : null
      };

      await dispatch(createVacancy({ organisationId: orgId, vacancy: formattedData })).unwrap();
      message.success('Вакансия успешно создана');

      setCurrentPage(1);
      const orgIdToFetch = showAllVacancies ? "all" : orgId;
      const skip = 0;
      dispatch(fetchVacancies({ skip, limit: pageSize, org_id: orgIdToFetch }));
    } catch (error) {
      message.error('Ошибка при создании вакансии');
      throw error;
    }
  };

  const handleUpdate = async (id, orgId, formData) => {
    try {
      await dispatch(updateVacancy({ id, organisationId: orgId, vacancy: formData })).unwrap();
      setIsEditModalOpen(false);
      setIsCreateFromTemplateModalOpen(false);

      const orgIdToFetch = showAllVacancies ? "all" : orgId;
      const skip = (currentPage - 1) * pageSize;
      dispatch(fetchVacancies({ skip, limit: pageSize, org_id: orgIdToFetch }));
    } catch (error) {
      message.error('Ошибка при обновлении вакансии');
    }
  };

  const handleDelete = async (id) => {
    const vacancy = vacancies.find(v => v.id === id);

    if (!vacancy || !vacancy.is_template) {
      message.error('Удаление доступно только для черновиков');
      return;
    }

    try {
      await dispatch(deleteVacancy({ id, organisationId: orgId })).unwrap();
      message.success('Вакансия удалена');

      const orgIdToFetch = showAllVacancies ? "all" : orgId;
      const skip = (currentPage - 1) * pageSize;
      dispatch(fetchVacancies({ skip, limit: pageSize, org_id: orgIdToFetch }));
    } catch (error) {
      message.error('Ошибка при удалении вакансии');
    }
  };

  const handleView = (vacancy) => {
    setSelectedVacancy(vacancy);
    setIsViewModalOpen(true);
  };

  const handleEdit = (vacancy) => {
    const { canEdit, reason } = canEditVacancy(role);
    if (!canEdit) {
      message.warning(reason);
      return;
    }
    setSelectedVacancy(vacancy);
    setIsEditModalOpen(true);
  };

  const handleStatusChange = async (item, newStatus, newIndex) => {
    if (!canChangeVacancyStatus(role)) {
      message.warning('У вас нет прав для изменения статуса вакансии');
      return;
    }

    const originalStatus = item.status;

    try {
      dispatch(updateVacancyStatusOptimistic({ id: item.id, status: newStatus }));

      await dispatch(updateVacancyStatus({
        id: item.id,
        organisationId: orgId,
        status: newStatus
      })).unwrap();

      message.success('Статус вакансии обновлён');
    } catch (error) {
      dispatch(revertVacancyStatus({ id: item.id, originalStatus }));
      message.error('Ошибка при обновлении статуса');
    }
  };

  const handleCommentSubmit = async (id, comment) => {
    try {
      await dispatch(updateVacancy({ id, organisationId: orgId, vacancy: { comment } })).unwrap();
      message.success('Комментарий добавлен');
      if (selectedVacancy?.id === id) {
        await dispatch(fetchVacancyHistory({ id, organisationId: orgId })).unwrap();
      }
    } catch (error) {
      message.error('Ошибка при добавлении комментария');
    }
  };

  const handleReminderSubmit = (id, datetime) => {
    console.log(`Reminder set for vacancy ${id} at`, datetime.toISOString());
  };

  const refreshVacancyHistory = async () => {
    if (!selectedVacancy?.id) return;
    try {
      await dispatch(fetchVacancyHistory({ id: selectedVacancy.id, organisationId: orgId })).unwrap();
    } catch (error) {
    }
  };

  const refreshVacancyCandidates = async () => {
    if (!selectedVacancy?.id) return;
    try {
      await dispatch(fetchCandidatesByVacancy(selectedVacancy.id)).unwrap();
    } catch (error) {
    }
  };

  const activeVacancies = vacancies.filter(v => !v.is_template);
  const vacanciesTemplate = vacancies.filter(v => v.is_template);
  const loading = vacanciesLoading || orgsLoading || usersLoading;

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <h1 style={{ margin: '0px', marginRight: 'auto', paddingRight: '20px' }}>Вакансии</h1>
        <Space wrap>
          {(role === 'superadmin' || role === 'admin') && (
            <Tooltip title={showAllVacancies ? "Вернуться к просмотру вакансий только вашей организации" : "Показать вакансии из всех организаций"}>
              <Button
                onClick={() => {
                  dispatch(setShowAllVacancies(!showAllVacancies));
                }}
                type={showAllVacancies ? "primary" : "default"}
              >
                {showAllVacancies ? "Показать только мои вакансии" : "Получить вакансии по всем организациям"}
              </Button>
            </Tooltip>
          )}
          {viewMode === 'list' ? (
            <>
              <Tooltip title="Настроить отображение колонок в таблице">
                <Popover
                  open={columnsDropdownOpen}
                  onOpenChange={setColumnsDropdownOpen}
                  content={
                    <div style={{ maxHeight: '70vh', overflowY: 'auto', minWidth: '200px' }}>
                      {allColumnKeys.map(key => (
                        <div key={key} style={{ padding: '4px 0' }}>
                          <Checkbox
                            checked={selectedColumns.includes(key)}
                            onChange={(e) => {
                              setSelectedColumns(prev => {
                                return prev.includes(key)
                                  ? prev.filter(col => col !== key)
                                  : [...prev, key];
                              });
                            }}
                          >
                            {columnLabels[key]}
                          </Checkbox>
                        </div>
                      ))}
                    </div>
                  }
                  trigger="click"
                  placement="bottomLeft"
                >
                  <Button icon={<SettingOutlined />}>
                    Настройка колонок
                  </Button>
                </Popover>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Настроить отображение статусов в канбане">
              <Popover
                open={statusesDropdownOpen}
                onOpenChange={setStatusesDropdownOpen}
                content={
                  <div style={{ maxHeight: '70vh', overflowY: 'auto', minWidth: '200px' }}>
                    {Object.keys(STATUSES)
                      .filter(key => key !== 'template')
                      .map(key => (
                        <div key={key} style={{ padding: '4px 0' }}>
                          <Checkbox
                            checked={selectedStatuses.includes(key)}
                            onChange={(e) => {
                              setSelectedStatuses(prev => {
                                return prev.includes(key)
                                  ? prev.filter(col => col !== key)
                                  : [...prev, key];
                              });
                            }}
                          >
                            {STATUSES[key].title}
                          </Checkbox>
                        </div>
                      ))}
                  </div>
                }
                trigger="click"
                placement="bottomLeft"
              >
                <Button icon={<SettingOutlined />}>
                  Настройка канбана
                </Button>
              </Popover>
            </Tooltip>
          )}

          <Radio.Group
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            buttonStyle="solid"
          >
            <Tooltip title="Просмотр вакансий в виде таблицы">
              <Radio.Button value="list">Список</Radio.Button>
            </Tooltip>
            <Tooltip title="Просмотр вакансий в виде канбан-доски">
              <Radio.Button value="kanban">Канбан</Radio.Button>
            </Tooltip>
          </Radio.Group>

          <Dropdown
            overlay={
              <Menu
                onClick={({ key }) => {
                  switch (key) {
                    case 'new':
                      if (canCreateVacancyWithoutRequest(role)) {
                        setIsCreateModalOpen(true);
                      } else {
                        message.warning('У вас нет прав для создания вакансии без заявки');
                      }
                      break;
                    // case 'from_request':
                    //   if (canCreateVacancyFromRequest(role)) {
                    //     message.info('Создание на основании заявки — еще не реализовано');
                    //   } else {
                    //     message.warning('У вас нет прав для создания вакансии из заявки');
                    //   }
                    //   break;
                    case 'from_draft':
                      setIsCreateFromTemplateModalOpen(true);
                      break;
                    default:
                      break;
                  }
                }}
              >
                {canCreateVacancyWithoutRequest(role) && (
                  <Menu.Item key="new">Новая</Menu.Item>
                )}
                {/* {canCreateVacancyFromRequest(role) && (
                  <Menu.Item key="from_request" disabled>Создать на основании заявки</Menu.Item>
                )} */}
                <Menu.Item key="from_draft">Создать из черновика</Menu.Item>
              </Menu>
            }
          >
            <Tooltip title={canCreateVacancyWithoutRequest(role) ? "Создать новую вакансию" : canCreateVacancyFromRequest(role) ? "Создать вакансию на основе заявки" : "У вас нет прав для создания вакансии"}>
              <Button type="primary" disabled={!canCreateVacancyWithoutRequest(role) && !canCreateVacancyFromRequest(role)}>
                Создать <DownOutlined />
              </Button>
            </Tooltip>
          </Dropdown>

        </Space>
      </div>

      {
        loading ? (
          <div>Загрузка...</div>
        ) : viewMode === 'list' ? (
          <ListView
            vacancies={vacancies}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            organisations={organisations}
            users={users}
            visibleColumns={selectedColumns}
            userRole={role}
            currentPage={currentPage}
            pageSize={pageSize}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={(page, size) => {
              setCurrentPage(page);
              if (size !== pageSize) {
                setPageSize(size);
                setCurrentPage(1);
              }
            }}
            filters={filters}
            onFiltersChange={setFilters}
          />
        ) : (
          <KanbanView
            vacancies={activeVacancies}
            onStatusChange={handleStatusChange}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            organisations={organisations}
            users={users}
            STATUSES={STATUSES}
            selectedStatuses={selectedStatuses}
            userRole={role}
          />
        )
      }

      <CreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreate}
        organisations={organisations}
        users={users}
      />

      <EditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        vacancy={selectedVacancy}
        organisations={organisations}
        users={users}
        onSubmit={handleUpdate}
      />

      <CreateFromTemplateModal
        isOpen={isCreateFromTemplateModalOpen}
        onClose={() => setIsCreateFromTemplateModalOpen(false)}
        vacanciesTemplate={vacanciesTemplate}
        onSubmit={handleUpdate}
        organisations={organisations}
        users={users}
      />

      <ViewModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        onCommentSubmit={handleCommentSubmit}
        onReminderSubmit={handleReminderSubmit}
        vacancy={selectedVacancy}
        organisations={organisations}
        users={users}
        funnelStatuses={funnelStatuses}
        history={history}
        historyLoading={!!vacanciesIsLoading}
        allVacancies={vacancies}
        candidatesByVacancy={candidatesByVacancy}
        allCandidates={allCandidates}
        candidatesLoading={candidatesLoading}
        funnelConfig={vacancyFunnelStatuses}
        funnelConfigLoading={!!vacanciesIsLoading}
        funnelConfigError={vacanciesError}
        orgId={orgId}
        refreshVacancyHistory={refreshVacancyHistory}
        refreshVacancyCandidates={refreshVacancyCandidates}
        fetchFunnelConfig={() => {
          if (selectedVacancy?.id) dispatch(fetchVacancyFunnelStatusesById(selectedVacancy.id));
        }}
        refreshFunnelConfig={() => {
          if (selectedVacancy?.id) dispatch(fetchVacancyFunnelStatusesById(selectedVacancy.id));
        }}
      />
      <Outlet />
    </div >
  );
};

export default VacanciesPage;